/**
 * The controls shared by every chart that moves through time (TRA-1297,
 * TRA-1592): an arrow that shows earlier or later times, and the line that
 * says which stretch is shown once it no longer ends at now, with the way
 * back, and the button out of a drag zoom. The replicas chart puts the
 * arrows on its edges; the metrics page puts them on every chart's edges
 * too (ChartControls) and moves every card with them.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react'

import type { PanDirection } from '@/components/models/home/replicaPan'
import type { TimeRange } from '@/components/models/home/replicaZoom'
import { formatSpanTime } from '@/components/models/home/scalingWindow'
import { Button } from '@/components/ui/button'

/** One arrow, the shape of the Trajectories table's side-scroll arrows (TRA-1166). */
export function PanArrow({
  direction,
  disabled,
  onClick,
  className = '',
}: {
  direction: PanDirection
  disabled: boolean
  onClick: () => void
  /** Where the arrow sits; the replicas chart pins it to an edge of the plot. */
  className?: string
}): React.JSX.Element {
  const earlier = direction === 'earlier'
  const label = earlier ? 'Show earlier' : 'Show later'
  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      aria-label={label}
      title={label}
      disabled={disabled}
      className={`h-7 w-7 ${className}`}
      onClick={onClick}
    >
      {earlier ? <ChevronLeft aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
    </Button>
  )
}

/**
 * The way out of a zoom. Sized to sit flush with the h-6 window pills on
 * the replicas chart, which pins it over the plot; the metrics page puts
 * it next to its arrows at their height.
 */
export function ResetZoomButton({
  onClick,
  className = '',
}: {
  onClick: () => void
  className?: string
}): React.JSX.Element {
  return (
    <Button
      variant="outline"
      size="sm"
      className={`rounded-sm px-2.5 font-mono text-[10px] uppercase tracking-wider ${className}`}
      onClick={onClick}
    >
      Reset zoom
    </Button>
  )
}

/** One muted line under a chart or a header. */
export function Note({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <p className="mt-2 text-xs text-muted-foreground">{children}</p>
}

/** Which stretch is shown once it no longer ends at now, and the way back. */
export function ShownLine({
  shown,
  onBackToNow,
}: {
  shown: TimeRange
  onBackToNow: () => void
}): React.JSX.Element {
  return (
    <Note>
      Showing {formatSpanTime(shown.start, shown)} to {formatSpanTime(shown.end, shown)}.{' '}
      <button
        type="button"
        className="underline underline-offset-4 hover:text-foreground"
        onClick={onBackToNow}
      >
        Back to now
      </button>
    </Note>
  )
}

/**
 * Where the metrics page looks and how to move it (TRA-1592): the same for
 * every chart on the page, so a click on any chart moves all of them.
 */
export interface PagePan {
  canEarlier: boolean
  canLater: boolean
  earlier: () => void
  later: () => void
  /** The page is zoomed or moved back, so Reset zoom shows. */
  showReset: boolean
  /** Back to the window at now. */
  reset: () => void
}

/**
 * The metrics page's controls on one chart: the arrows on the plot's edges,
 * shown while the pointer is over the chart (or an arrow has focus), and
 * Reset zoom at the plot's top right while the page is zoomed or moved. The
 * parent is the plot's `relative group` box.
 */
export function ChartControls({ pan }: { pan: PagePan }): React.JSX.Element {
  const edge = 'pointer-events-auto absolute top-1/2 -translate-y-1/2'
  return (
    <>
      <div className="pointer-events-none absolute inset-0 z-10 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        <PanArrow direction="earlier" disabled={!pan.canEarlier} onClick={pan.earlier} className={`${edge} left-1`} />
        <PanArrow direction="later" disabled={!pan.canLater} onClick={pan.later} className={`${edge} right-1`} />
      </div>
      {pan.showReset && <ResetZoomButton onClick={pan.reset} className="absolute right-1 top-0 z-10 h-6" />}
    </>
  )
}
